import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  getMunicipiosPorRegion,
  getRegiones,
  findMunicipio,
} from "../utils/municipiosHelper";
import { motion } from "framer-motion";
import { FaFilePdf, FaFileWord, FaFileImage } from "react-icons/fa";

export default function DescargasMunicipios() {
  const { regionId } = useParams();
  const [region, setRegion] = useState(regionId || "costalegre");
  const [query, setQuery] = useState("");
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (regionId) setRegion(regionId);
  }, [regionId]);

  const municipios = getMunicipiosPorRegion(region);

  const handleBuscar = (value) => {
    setQuery(value);
    if (!value.trim()) {
      setResultado(null);
      return;
    }
    const municipio = findMunicipio(value);
    setResultado(municipio);
  };

  // Icono por tipo de documento
  const getIcon = (tipo) => {
    switch (tipo.toLowerCase()) {
      case "pdf":
        return <FaFilePdf className="text-red-500 text-xl" />;
      case "word":
        return <FaFileWord className="text-blue-500 text-xl" />;
      case "svg":
        return <FaFileImage className="text-green-500 text-xl" />;
      default:
        return <FaFilePdf className="text-gray-500 text-xl" />;
    }
  };

  return (
    <div className="bg-gray-50 text-gray-800 min-h-screen">
      {/* Hero Banner */}
      <div
        className="h-[50vh] bg-cover bg-center relative flex items-center justify-center"
        style={{ backgroundImage: `url('/images/${region}-banner.jpg')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-sky-900/70 to-black/60"></div>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center text-white px-4"
        >
          <h1 className="text-4xl md:text-6xl font-extrabold drop-shadow-lg">
            Descargas por Municipio
          </h1>
          <p className="mt-4 text-lg md:text-xl max-w-2xl mx-auto text-gray-200">
            Consulta documentos oficiales de la región{" "}
            <span className="capitalize font-semibold">{region}</span>.
          </p>
        </motion.div>
      </div>

      {/* Buscador */}
      <div className="max-w-4xl mx-auto -mt-12 relative z-20">
        <div className="bg-white shadow-xl rounded-2xl p-6">
          <h2 className="text-xl font-bold text-sky-700 mb-4">🔎 Buscar Municipio</h2>
          <input
            type="text"
            value={query}
            onChange={(e) => handleBuscar(e.target.value)}
            placeholder="Escribe el nombre del municipio..."
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-sky-500 focus:outline-none"
          />

          {/* Sugerencias */}
          {query && !resultado && (
            <ul className="mt-3 bg-gray-100 border rounded-lg shadow divide-y max-h-40 overflow-y-auto">
              {municipios
                .filter((m) =>
                  m.nombre.toLowerCase().includes(query.toLowerCase())
                )
                .map((m, idx) => (
                  <li
                    key={idx}
                    className="px-4 py-2 hover:bg-sky-100 cursor-pointer"
                    onClick={() => {
                      setQuery(m.nombre);
                      setResultado(m);
                    }}
                  >
                    {m.nombre}
                  </li>
                ))}
            </ul>
          )}

          {/* Resultado */}
          {resultado && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 p-4 bg-sky-50 border rounded-lg shadow"
            >
              <h3 className="font-semibold text-lg text-sky-700">
                {resultado.nombre} ({resultado.region})
              </h3>
              <ul className="mt-2 space-y-1">
                {resultado.documentos.map((doc, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    {getIcon(doc.tipo)}
                    <a
                      href={doc.url}
                      className="text-sky-600 hover:underline"
                      download
                    >
                      {doc.titulo}
                    </a>
                    <span className="text-gray-500 text-sm">({doc.tipo})</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </div>
      </div>

      {/* Selector de región */}
      <div className="max-w-6xl mx-auto px-6 mt-16">
        <label className="block mb-2 font-semibold text-gray-700">
          Cambiar región:
        </label>
        <select
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          className="p-3 border rounded-lg shadow focus:ring-2 focus:ring-sky-500"
        >
          {getRegiones().map((r) => (
            <option key={r.id} value={r.id}>
              {r.nombre}
            </option>
          ))}
        </select>
      </div>

      {/* Grid de municipios */}
      <div className="max-w-6xl mx-auto px-6 py-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {municipios.map((m, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            whileHover={{ scale: 1.03 }}
            className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 flex flex-col justify-between"
          >
            <div>
              <h2 className="text-xl font-bold text-sky-700 mb-4">{m.nombre}</h2>
              <ul className="space-y-2">
                {m.documentos.map((doc, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-gray-700"
                  >
                    {getIcon(doc.tipo)}
                    <a
                      href={doc.url}
                      download
                      className="hover:text-sky-600 hover:underline"
                    >
                      {doc.titulo}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
